import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { INTEL_API, errMsg } from "./OutreachSendButton";

// Questions Partner Intelligence (v2.1.6.4.1.4) : le système ne pose une question que si une information HUMAINE peut modifier sensiblement le positionnement d'un message
// et qu'il n'a pas pu la retrouver (faits mémorisés, interactions, anciens outreach, CRM, contenus ITERCATUS, sources officielles).
// Contrat API : GET /partner-intel/questions?status=OPEN · POST /partner-intel/questions/{id}/answer {answer: yes|no|unknown|<choix>|<texte>, resume}
// La réponse est mémorisée (human_confirmed + qui + quand) : la même question n'est jamais reposée. Rien n'est envoyé.
const EP = `${INTEL_API}/editorial/partner-intel`;
const YN = [["yes", "Oui"], ["no", "Non"]];
const ANSWER_LABEL = { yes: "Oui", no: "Non", unknown: "Je ne sais pas" };

// « Modifier ma réponse » : l'ancienne réponse et sa date sont CONSERVÉES côté serveur ; le dossier est réévalué (mail encore modifiable régénéré, jamais envoyé).
export const AnswerEditor = ({ q, onDone }) => {
  const [val, setVal] = useState("");
  const send = async (answer) => {
    try { const r = await axios.post(`${EP}/questions/${q.question_id}/modify`, { answer, reassess: true }); toast.success(r.data.mail_version ? `Réponse corrigée — nouvelle version du message (v${r.data.mail_version})` : "Réponse corrigée"); onDone?.(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`intel-answer-editor-${q.question_id}`}>
      {q.answer_type === "yes_no_unknown" && [["yes", "Oui"], ["no", "Non"], ["unknown", "Je ne sais pas"]].map(([v, l]) => <Button key={v} size="sm" variant="outline" disabled={q.answer === v} onClick={() => send(v)}>{l}</Button>)}
      {q.answer_type === "choice" && <>{(q.choices || []).map((c) => <label key={c.value} className="text-sm flex items-center gap-1"><input type="radio" name={`m-${q.question_id}`} checked={val === c.value} onChange={() => setVal(c.value)} />{c.label}</label>)}<Button size="sm" disabled={!val} onClick={() => send(val)}>Valider ma nouvelle réponse</Button></>}
      {q.answer_type === "text" && <><Input className="max-w-xl" placeholder="Nouvelle réponse" value={val} onChange={(e) => setVal(e.target.value)} /><Button size="sm" disabled={!val} onClick={() => send(val)}>Valider ma nouvelle réponse</Button></>}
    </div>
  );
};

const QuestionCard = ({ q, onDone }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (answer) => {
    setBusy(true);
    try {
      const r = await axios.post(`${EP}/questions/${q.question_id}/answer`, { answer, resume: true });
      toast.success(r.data.run ? `Réponse enregistrée — exécution reprise : ${r.data.run.final_status}` : "Réponse enregistrée");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-lg p-3 bg-white space-y-2" data-testid={`intel-partner-question-${q.question_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge className="bg-red-100 text-red-900">{q.level}</Badge>
        <span><b>Organisation :</b> {q.organization}</span>
        {q.contact && <span><b>Contact :</b> {q.contact}{q.contact_role ? ` — ${q.contact_role}` : ""}</span>}
        {q.opportunity && <span><b>Opportunité :</b> {q.opportunity}</span>}
      </div>
      <p className="font-semibold" data-testid="intel-partner-question-text">{q.question}</p>
      <p className="text-xs text-muted-foreground"><b>Pourquoi cette information est utile :</b> {q.why}</p>
      <div className="flex flex-wrap items-center gap-2">
        {q.answer_type === "yes_no_unknown" && YN.map(([v, l]) => <label key={v} className="text-sm flex items-center gap-1"><input type="radio" name={q.question_id} checked={val === v} onChange={() => setVal(v)} />{l}</label>)}
        {q.answer_type === "choice" && (q.choices || []).map((c) => <label key={c.value} className="text-sm flex items-center gap-1"><input type="radio" name={q.question_id} checked={val === c.value} onChange={() => setVal(c.value)} />{c.label}</label>)}
        {q.answer_type === "text" && <Input className="max-w-xl" placeholder="Votre réponse" value={val} onChange={(e) => setVal(e.target.value)} data-testid="intel-partner-question-answer" />}
        <Button size="sm" disabled={!val || busy} onClick={() => send(val)} data-testid="intel-partner-question-validate">Valider</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => send("unknown")} data-testid="intel-partner-question-unknown">Je ne sais pas</Button>
      </div>
    </div>
  );
};

export const PartnerQuestionsQueue = ({ onChange }) => {
  const [data, setData] = useState({ open: 0, questions: [] });
  const [done, setDone] = useState([]);
  const load = useCallback(async () => {
    try {
      setData((await axios.get(`${EP}/questions`, { params: { status: "OPEN" } })).data);
      setDone((await axios.get(`${EP}/questions`, { params: { status: "ANSWERED,IGNORED" } })).data.questions);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="intel-partner-questions-queue">
      <h2 className="text-lg font-semibold" data-testid="intel-partner-questions-count">{data.open} question{data.open > 1 ? "s" : ""} Partner Intelligence</h2>
      {data.questions.length === 0 && <p className="text-sm text-muted-foreground">Aucune question en attente.</p>}
      {data.questions.map((q) => <QuestionCard key={q.question_id} q={q} onDone={() => { load(); onChange?.(); }} />)}
      {done.length > 0 && (
        <section className="space-y-2" data-testid="intel-partner-questions-done">
          <h3 className="text-sm font-semibold">Questions déjà traitées ({done.length})</h3>
          {done.map((q) => (
            <div key={q.question_id} className="border rounded-lg p-3 bg-white space-y-1 text-sm" data-testid={`intel-partner-question-done-${q.question_id}`}>
              <p className="font-semibold">{q.organization}{q.contact ? ` — ${q.contact}` : ""}</p><p>{q.question}</p>
              <p className="text-xs text-muted-foreground">{q.status === "IGNORED" ? "Ignorée (non pertinent)" : `Réponse : ${ANSWER_LABEL[q.answer] || q.answer}`} · par {q.answered_by} le {q.answered_at?.slice(0, 10)}{(q.answer_history || []).length > 0 ? ` · ${(q.answer_history || []).length} réponse(s) précédente(s) conservée(s)` : ""}</p>
              <AnswerEditor q={q} onDone={() => { load(); onChange?.(); }} />
            </div>
          ))}
        </section>
      )}
    </div>
  );
};
